import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, FileText, Receipt, Camera, Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import CarPanelMap from "@/components/CarPanelMap";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import { PARTS, PART_BY_KEY } from "@/lib/parts";

const OBS_SUGGESTED = ["ventosas", "pintura rota", "grave", "nuevo"];

const EMPTY_LINE = (panel) => ({
  panel_number: panel.number,
  part_key: panel.key,
  part_name: panel.name,
  bollos: [],
  leve: [],
  medio: [],
  grave: [],
  price: 0,
  suggested_price: null,
  aluminium: false,
  obs_mode: "suggested",
  obs_tags: [],
  obs_text: "",
});

const sumArr = (a) => (a || []).reduce((s, x) => s + (Number(x) || 0), 0);

function CountChips({ values, onChange, testid }) {
  // compact dent-counter; values is array<int> — we sum them but allow editing as a single field for simplicity
  const total = sumArr(values);
  return (
    <Input
      type="number" min="0"
      className="h-8 w-20 text-center"
      data-testid={testid}
      value={total}
      onChange={(e) => onChange([Math.max(0, parseInt(e.target.value || "0", 10))])}
    />
  );
}

export default function PresupuestoDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [models, setModels] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfPath, setPdfPath] = useState(null);
  // ai_model_names stores the list of selected model names (multi-select)
  // ai_model_name (legacy single) is kept in quote for save compatibility
  const [selectedModelNames, setSelectedModelNames] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/quotes/${id}`);
      const q = r.data;
      setQuote(q);
      const [v, cs, ms] = await Promise.all([
        api.get(`/vehicles/${q.vehicle_id}`),
        api.get("/clients"),
        api.get("/ai/models"),
      ]);
      setVehicle(v.data); setClients(cs.data); setModels(ms.data);
      // Restore selected model names from saved quote
      const savedNames = q.ai_model_names || (q.ai_model_name ? [q.ai_model_name] : []);
      setSelectedModelNames(savedNames);
      if (q.client_id) {
        const c = cs.data.find(x => x.id === q.client_id);
        setClient(c || null);
      }
      const ph = await api.get(`/photos/by-vehicle/${q.vehicle_id}`);
      setPhotos(ph.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateQuote = (patch) => setQuote((q) => ({ ...q, ...patch }));

  const setLineForKey = (key, mutator) => {
    setQuote((q) => {
      const lines = [...(q.lines || [])];
      const idx = lines.findIndex(l => l.part_key === key);
      if (idx === -1) {
        const part = PART_BY_KEY[key];
        const fresh = EMPTY_LINE(part);
        lines.push(mutator(fresh));
      } else {
        lines[idx] = mutator({ ...lines[idx] });
      }
      return { ...q, lines };
    });
  };

  const removeLine = (key) => {
    setQuote((q) => ({ ...q, lines: (q.lines || []).filter(l => l.part_key !== key) }));
  };

  const lineForKey = (key) => quote?.lines?.find(l => l.part_key === key);

  const dotsByKey = useMemo(() => {
    if (!quote) return {};
    const r = {};
    for (const l of quote.lines || []) {
      const total = sumArr(l.bollos) + sumArr(l.leve) + sumArr(l.medio) + sumArr(l.grave);
      if (total > 0) r[l.part_key] = total;
    }
    return r;
  }, [quote]);

  const totals = useMemo(() => {
    if (!quote) return { subtotal: 0, vat: 0, total: 0 };
    let subtotal = Number(quote.disassembly_amount || 0);
    for (const l of quote.lines || []) subtotal += Number(l.price || 0);
    const vat = subtotal * Number(quote.vat_rate || 0) / 100;
    return { subtotal, vat, total: subtotal + vat };
  }, [quote]);

  const save = async () => {
    setSaving(true);
    try {
      // Persist selected model names alongside legacy single field
      const payload = {
        ...quote,
        ai_model_names: selectedModelNames,
        ai_model_name: selectedModelNames[0] || null,
      };
      await api.put(`/quotes/${id}`, payload);
      toast.success("Presupuesto guardado");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  // Auto-suggestion: whenever features change and models are selected, predict
  // price for each line. If multiple models are selected, the backend averages them.
  // For each model, the dent input is adapted based on model kind:
  //   - desglosado model: all dents count as 'grave'
  //   - global model: all dents summed into 'bollos'
  const suggestTimer = useRef(null);

  // Build the feature payload for a line given a specific model's kind
  const buildFeatures = (ln, modelKind, partKind) => {
    const bollos = (ln.bollos || []).reduce((s, x) => s + (Number(x) || 0), 0);
    const leve   = (ln.leve   || []).reduce((s, x) => s + (Number(x) || 0), 0);
    const medio  = (ln.medio  || []).reduce((s, x) => s + (Number(x) || 0), 0);
    const grave  = (ln.grave  || []).reduce((s, x) => s + (Number(x) || 0), 0);
    const totalDents = bollos + leve + medio + grave;
    if (totalDents === 0) return null;
    if (modelKind === "desglosado") {
      // All dents → grave
      return { part_kind: partKind, bollos: 0, leve: 0, medio: 0, grave: totalDents, aluminium: ln.aluminium ? 1 : 0 };
    }
    // global: all dents summed into bollos
    return { part_kind: partKind, bollos: totalDents, leve: 0, medio: 0, grave: 0, aluminium: ln.aluminium ? 1 : 0 };
  };

  useEffect(() => {
    if (!quote || selectedModelNames.length === 0) return;
    if (!quote.lines || quote.lines.length === 0) return;
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestTimer.current = setTimeout(async () => {
      try {
        // Collect model kinds for the selected models
        const modelKindMap = {};
        for (const m of models) { modelKindMap[m.name] = m.kind; }

        const next = await Promise.all(
          (quote.lines || []).map(async (ln) => {
                          const partMeta = PART_BY_KEY[ln.part_key];
              const partKind = partMeta?.part_kind || "aleta";

              // One prediction per selected model. We sum and divide by the
              // exact number of *successful* predictions (no phantom zero).
              let sum = 0;
              let count = 0;
              for (const mName of selectedModelNames) {
                const mKind = modelKindMap[mName] || "global";
                const feats = buildFeatures(ln, mKind, partKind);
                if (!feats) continue;
                try {
                  const r = await api.post("/ai/predict", {
                    model_name: mName,
                    ...feats,
                  });
                  const v = Number(r.data.predicted_price);
                  if (Number.isFinite(v)) {
                    sum += v;
                    count += 1;
                  }
                } catch (_) { /* skip this model */ }
              }
              const suggested = count > 0 ? sum / count : null;
            return { ...ln, suggested_price: suggested };
          })
        );
        const changed = next.some((l, i) =>
          (l.suggested_price ?? null) !== (quote.lines[i]?.suggested_price ?? null)
        );
        if (changed) setQuote((q) => ({ ...q, lines: next }));
      } catch (_) { /* ignore */ }
    }, 450);
    return () => suggestTimer.current && clearTimeout(suggestTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(selectedModelNames),
    JSON.stringify((quote?.lines || []).map(l => ({
      k: l.part_key, b: l.bollos, le: l.leve, m: l.medio, g: l.grave, al: l.aluminium,
    }))),
  ]);

  const convert = async () => {
    if (!quote.client_id) { toast.error("Asigna un cliente antes de convertir"); return; }
    if (!confirm("Convertir este presupuesto en factura?")) return;
    try {
      await api.put(`/quotes/${id}`, quote);
      const r = await api.post(`/quotes/${id}/convert`);
      toast.success(`Factura ${r.data.number || ""} creada`);
      nav(`/facturas/${r.data.invoice_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("vehicle_id", quote.vehicle_id); fd.append("file", file);
    try {
      await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ph = await api.get(`/photos/by-vehicle/${quote.vehicle_id}`);
      setPhotos(ph.data); toast.success("Foto subida");
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const deletePhoto = async (pid) => {
    if (!confirm("¿Borrar foto?")) return;
    try {
      await api.delete(`/photos/${pid}`);
      setPhotos((ps) => ps.filter(p => p.id !== pid));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!quote) return <div className="text-zinc-500">Cargando…</div>;

  const isInvoiced = !!quote.invoice_id;

  return (
    <div data-testid="presupuesto-detail" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => nav("/presupuestos")} className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-900 mb-2">
            <ArrowLeft size={12} /> Volver
          </button>
          <h1 className="section-title">Presupuesto <span className="font-mono text-xl ml-2">{quote.number}</span></h1>
          <div className="flex items-center gap-3 mt-2">
            <Label htmlFor="quote-date" className="text-xs text-zinc-500">Fecha</Label>
            <Input
              id="quote-date"
              type="date"
              className="h-8 w-[160px] text-sm"
              value={(quote.date || "").slice(0, 10)}
              onChange={(e) => {
                const v = e.target.value;
                // store as ISO datetime at noon UTC to avoid TZ drifting
                updateQuote({ date: v ? `${v}T12:00:00+00:00` : "" });
              }}
              data-testid="quote-date"
            />
            <Label htmlFor="quote-status" className="text-xs text-zinc-500 ml-2">Estado</Label>
            <Select value={quote.status || "draft"} onValueChange={(v) => updateQuote({ status: v })}>
              <SelectTrigger id="quote-status" data-testid="quote-status" className="h-8 w-[140px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="sent">Enviado</SelectItem>
                <SelectItem value="accepted">Aceptado</SelectItem>
                <SelectItem value="invoiced">Facturado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={save} disabled={saving} data-testid="btn-save-quote"><Save size={14} className="mr-1" />{saving ? "Guardando…" : "Guardar"}</Button>
          <Button variant="outline" onClick={() => setPdfPath(`/quotes/${id}/pdf`)} data-testid="btn-quote-pdf"><FileText size={14} className="mr-1" />PDF</Button>
          {!isInvoiced ? (
            <Button onClick={convert} data-testid="btn-convert"><Receipt size={14} className="mr-1" />Convertir en factura</Button>
          ) : (
            <Button variant="outline" onClick={() => nav(`/facturas/${quote.invoice_id}`)} data-testid="btn-go-invoice"><Receipt size={14} className="mr-1" />Ver factura</Button>
          )}
        </div>
      </div>

      {/* Header */}
      <section className="page-card grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <Label className="text-xs">Cliente</Label>
          <Select value={quote.client_id || "none"} onValueChange={(v) => updateQuote({ client_id: v === "none" ? null : v })}>
            <SelectTrigger data-testid="quote-client"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Sin cliente —</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-zinc-500 mt-3">
            Vehículo: <span className="font-medium text-zinc-900">{vehicle?.plate}</span><br />
            {vehicle?.brand} {vehicle?.model} · Color: {vehicle?.color || "—"}
          </div>
        </div>
        <div>
          <Label className="text-xs">Modelos IA (sólo interno)</Label>
          <div className="space-y-1 mt-1 max-h-40 overflow-y-auto border rounded-md p-2 border-[var(--rule)]" data-testid="quote-ai-models">
            {models.length === 0 && <p className="text-xs text-zinc-400">No hay modelos entrenados.</p>}
            {models.map(m => {
              const checked = selectedModelNames.includes(m.name);
              return (
                <label key={m.name} className="flex items-center gap-2 cursor-pointer py-0.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setSelectedModelNames(prev =>
                        checked ? prev.filter(n => n !== m.name) : [...prev, m.name]
                      );
                    }}
                    className="accent-zinc-900"
                  />
                  <span className="text-xs">{m.name}</span>
                  <span className={`text-[10px] px-1 rounded ml-auto ${m.kind === "global" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700"}`}>{m.kind}</span>
                </label>
              );
            })}
          </div>
          {selectedModelNames.length === 1 && (
            <p className="text-[11px] text-zinc-500 mt-1">1 modelo seleccionado.</p>
          )}
          {selectedModelNames.length > 1 && (
            <p className="text-[11px] text-zinc-500 mt-1">{selectedModelNames.length} modelos seleccionados — se promediará el resultado.</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <Switch id="sev" checked={!!quote.severity_split} onCheckedChange={(v) => updateQuote({ severity_split: v })} data-testid="toggle-severity" />
            <Label htmlFor="sev" className="text-sm">Separado por severidad</Label>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">
            IA: modelos <em>desglosado</em> reciben todos los golpes como <em>grave</em>; modelos <em>global</em> suman todos los golpes en bollos.
          </p>
        </div>
        <div>
          <Label className="text-xs">Notas generales</Label>
          <Textarea rows={4} value={quote.notes || ""} onChange={(e) => updateQuote({ notes: e.target.value })} data-testid="quote-notes" />
        </div>
      </section>

      {/* Car panel + lines */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="page-card">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Mapa del vehículo</div>
          <CarPanelMap activeKey={activeKey} onPick={(k) => setActiveKey(k)} dotsByKey={dotsByKey} />
          <p className="text-xs text-zinc-500 mt-3">Pulsa un número para añadir o editar esa parte. Los montantes se gestionan desde la lista.</p>
        </div>

        <div className="page-card">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Partes del coche</div>
          <div className="grid grid-cols-1 gap-2 max-h-[60vh] overflow-y-auto pr-1">
            {PARTS.map((p) => {
              const ln = lineForKey(p.key);
              const isOn = !!ln;
              const total = ln ? sumArr(ln.bollos) + sumArr(ln.leve) + sumArr(ln.medio) + sumArr(ln.grave) : 0;
              return (
                <div key={p.key} className={`border rounded-lg ${isOn ? "border-zinc-300" : "border-[var(--rule)]"} p-3`} data-testid={`part-row-${p.key}`}>
                  <div
                    className={`flex items-center justify-between gap-2 ${isOn ? "cursor-pointer" : ""}`}
                    onClick={() => {
                      if (!isOn) return;
                      setActiveKey(activeKey === p.key ? null : p.key);
                    }}
                    data-testid={`part-row-header-${p.key}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-xs font-mono text-zinc-500 w-6">{p.number ? `#${p.number}` : "—"}</span>
                      <span className="font-medium text-sm">{p.name}</span>
                      {isOn && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 text-xs">
                          {total} bollo{total === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                      {isOn ? (
                        <>
                          <div className="flex items-center gap-1" title="Precio de la parte">
                            <Label htmlFor={`inline-price-${p.key}`} className="text-xs text-zinc-500">Precio</Label>
                            <Input
                              id={`inline-price-${p.key}`}
                              type="number"
                              step="0.01"
                              inputMode="decimal"
                              value={ln.price}
                              onChange={(e) => setLineForKey(p.key, l => ({ ...l, price: parseFloat(e.target.value || "0") }))}
                              className="h-7 w-24 text-sm"
                              data-testid={`inline-price-${p.key}`}
                              aria-label="Precio de la parte en euros"
                            />
                            <span className="text-xs text-zinc-500">€</span>
                          </div>
                          <button
                            onClick={() => setActiveKey(activeKey === p.key ? null : p.key)}
                            className="text-xs text-zinc-600 hover:text-zinc-900"
                            data-testid={`edit-part-${p.key}`}
                            title={activeKey === p.key ? "Cerrar" : "Editar"}
                          >
                            {activeKey === p.key ? "Cerrar" : "Editar"}
                          </button>
                          <button onClick={() => { removeLine(p.key); if (activeKey === p.key) setActiveKey(null); }} className="text-zinc-500 hover:text-red-600" title="Quitar">
                            <Trash2 size={14} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => { setLineForKey(p.key, x => x); setActiveKey(p.key); }} className="text-xs inline-flex items-center gap-1 text-zinc-700 hover:text-zinc-900" data-testid={`add-part-${p.key}`}>
                          <Plus size={12} /> Añadir
                        </button>
                      )}
                    </div>
                  </div>

                  {isOn && (activeKey === p.key) && (
                    <div className="mt-3 space-y-3">
                      {!quote.severity_split ? (
                        <div className="flex items-center gap-3">
                          <Label className="text-xs w-20">Bollos</Label>
                          <CountChips values={ln.bollos} onChange={(v) => setLineForKey(p.key, l => ({ ...l, bollos: v }))} testid={`bollos-${p.key}`} />
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {["leve", "medio", "grave"].map((sev) => (
                            <div key={sev}>
                              <Label className="text-xs capitalize">{sev}</Label>
                              <CountChips values={ln[sev]} onChange={(v) => setLineForKey(p.key, l => ({ ...l, [sev]: v }))} testid={`${sev}-${p.key}`} />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Label className="text-xs">Precio (€)</Label>
                          <Input type="number" step="0.01" value={ln.price}
                                 onChange={(e) => setLineForKey(p.key, l => ({ ...l, price: parseFloat(e.target.value || "0") }))}
                                 data-testid={`price-${p.key}`} />
                        </div>
                        {ln.suggested_price != null && (
                          <div className="text-xs text-zinc-400 whitespace-nowrap pt-5">sugerido: {ln.suggested_price.toFixed(2)} €</div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch checked={!!ln.aluminium} onCheckedChange={(v) => setLineForKey(p.key, l => ({ ...l, aluminium: v }))} id={`alu-${p.key}`} />
                        <Label htmlFor={`alu-${p.key}`} className="text-xs">Aluminio</Label>
                      </div>

                      <div>
                        <Label className="text-xs">Observaciones</Label>
                        <div className="flex gap-2 my-2">
                          <button type="button" className={`chip ${ln.obs_mode === "suggested" ? "is-selected" : ""}`} onClick={() => setLineForKey(p.key, l => ({ ...l, obs_mode: "suggested", obs_text: "" }))}>Sugeridas</button>
                          <button type="button" className={`chip ${ln.obs_mode === "free" ? "is-selected" : ""}`} onClick={() => setLineForKey(p.key, l => ({ ...l, obs_mode: "free", obs_tags: [] }))}>Texto libre</button>
                        </div>
                        {ln.obs_mode === "suggested" ? (
                          <div className="flex gap-2 flex-wrap" data-testid={`obs-suggested-${p.key}`}>
                            {OBS_SUGGESTED.map(tag => {
                              const active = ln.obs_tags.includes(tag);
                              return (
                                <button key={tag} type="button"
                                  className={`chip ${active ? "is-selected" : ""}`}
                                  onClick={() => setLineForKey(p.key, l => ({
                                    ...l,
                                    obs_tags: active ? l.obs_tags.filter(t => t !== tag) : [...l.obs_tags, tag],
                                  }))}>
                                  {tag}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <Textarea rows={2} value={ln.obs_text}
                            onChange={(e) => setLineForKey(p.key, l => ({ ...l, obs_text: e.target.value }))}
                            data-testid={`obs-text-${p.key}`} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disassembly + totals + photos */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="page-card lg:col-span-1">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Desmontaje y totales</div>
          <Label className="text-xs">Desmontaje (€)</Label>
          <Input type="number" step="0.01" value={quote.disassembly_amount || 0}
                 onChange={(e) => updateQuote({ disassembly_amount: parseFloat(e.target.value || "0") })}
                 data-testid="quote-disassembly" />
          <Label className="text-xs mt-3 block">IVA (%)</Label>
          <Input type="number" step="0.5" value={quote.vat_rate}
                 onChange={(e) => updateQuote({ vat_rate: parseFloat(e.target.value || "0") })} />

          <div className="mt-6 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{totals.subtotal.toFixed(2)} €</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">IVA ({quote.vat_rate}%)</span><span>{totals.vat.toFixed(2)} €</span></div>
            <div className="flex justify-between font-display text-2xl pt-2 border-t border-[var(--rule)] mt-2">
              <span>Total</span><span data-testid="quote-total">{totals.total.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        <div className="page-card lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Fotos del expediente</div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} data-testid="photo-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-upload-photo">
              <Camera size={14} className="mr-1" /> Subir foto
            </Button>
          </div>
          {photos.length === 0 ? (
            <div className="text-sm text-zinc-400 py-8 text-center">Sin fotos. Las fotos se comparten automáticamente con la factura.</div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map(p => (
                <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-full h-28 object-cover rounded border border-[var(--rule)]" />
                  <button onClick={() => deletePhoto(p.id)} className="absolute top-1 right-1 bg-white/90 rounded p-1 opacity-0 group-hover:opacity-100 transition" title="Borrar">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <PdfPreviewDialog path={pdfPath} filename={`presupuesto_${quote.number}.pdf`} onClose={() => setPdfPath(null)} />
    </div>
  );
}
